// @file A fat module with
// - Post CRUD-related functions acting as a black-box to backend APIs for post operations.
// - Helpers related to abovementioned purpose.
import { PostColor } from '@padlet/arvo'
import { captureMessage } from 'bits/error_tracker'
import { isAppUsing } from 'bits/flip'
import PromiseQueue from 'bits/promise_queue'
import { SAVE_CHANGES_FAILED } from 'bits/snackbar_helper'
import { isUrl } from 'bits/url'
import cloneDeep from 'lodash.clonedeep'
import debounce from 'lodash.debounce'
import uniqueId from 'lodash.uniqueid'
import PadletApi from 'surface/padlet_api'
import { Cid, Id, Post, PostAttributes, UpdatePostArgs } from 'types'
import { Commit, Dispatch } from 'vuex'

/********************************
 * CONSTANTS
 ********************************/
const PASTEABLE_ATTRIBUTES = ['subject', 'body', 'attachment', 'color']
const VALID_COLORS: PostColor[] = ['red', 'orange', 'green', 'blue', 'purple']
const COPIED_POST_HASH_KEY = 'padlet'
const WHITESPACE_REGEXT = /^\s*$/

/********************************
 * HELPERS
 ********************************/
// HELPERS
const isNew = (post: Post | PostAttributes): boolean => !post.id
const isEmpty = (post: PostAttributes | undefined): boolean =>
  !post ||
  ((!post.subject || WHITESPACE_REGEXT.test(post.subject)) &&
    (!post.body || WHITESPACE_REGEXT.test(post.body)) &&
    (!post.attachment || WHITESPACE_REGEXT.test(post.attachment)))
const isNonEmpty = (post: PostAttributes): boolean => !isEmpty(post)
const isColorValid = (color: PostColor): boolean => VALID_COLORS.includes(color)
const hasAttachment = (post: Post): boolean => {
  return !!post.wish_content?.attachment_props || !!post.attachment
}

function dumpPostContentHashString(post: PostAttributes): string {
  const attributes = PASTEABLE_ATTRIBUTES.reduce((hash, key) => {
    if (post[key]) hash[key] = post[key]
    return hash
  }, {})
  return Object.keys(attributes).length ? JSON.stringify({ [COPIED_POST_HASH_KEY]: attributes }) : ''
}

function isPasteablePost(postAttributes: PostAttributes): boolean {
  const hasDisallowedAttribute = Object.keys(postAttributes).some(
    (attribute: string): boolean => !PASTEABLE_ATTRIBUTES.includes(attribute),
  )
  if (hasDisallowedAttribute) return false
  if (isEmpty(postAttributes)) return false
  if (postAttributes.color && !isColorValid(postAttributes.color)) return false
  if (postAttributes.attachment && !isUrl(postAttributes.attachment)) return false
  return true
}

function loadPostContentHashString(text: string): PostAttributes | null {
  try {
    const parsedItem = JSON.parse(text)
    if (typeof parsedItem !== 'object') return null
    const parsedItemValue = parsedItem[COPIED_POST_HASH_KEY]
    if (!parsedItemValue || !isPasteablePost(parsedItemValue)) return null
    return parsedItem.padlet as PostAttributes
  } catch {
    return null
  }
}

function getPostCidsToDeleteInPage(
  isFirstPage: boolean,
  isLastPage: boolean,
  newPosts: any[],
  newPostEntitiesByCid: any,
): Cid[] {
  // the array returned from the back end is guaranteed to be ordered by id (DESC)
  const newPostIdList = newPosts.map((post) => post.attributes.id)

  // last page should cover the smallest post id
  const lookupRangeStart = isLastPage ? 0 : newPostIdList[newPosts.length - 1]

  // first page should cover the biggest post id
  const lookupRangeEnd = isFirstPage ? Infinity : newPostIdList[0]

  return Object.keys(newPostEntitiesByCid).filter((postCid: string): boolean => {
    const post = newPostEntitiesByCid[postCid]

    // Keep unsaved posts
    if (isNew(post)) {
      return false
    }

    const postId = newPostEntitiesByCid[postCid].id

    // Keep everything not inside the page's range
    if (postId > lookupRangeEnd || postId < lookupRangeStart) {
      return false
    }

    // Anything that's inside the page's range but not in newPostIdList
    // means they are deleted in the backend but the front end does not know yet
    return !newPostIdList.includes(postId)
  })
}

// reconcile newly fetched post data with current state to get the correct state
function reconcilePostsAfterFetch(context: any, newPosts: any[], options: Record<string, any>): Record<Cid, Post> {
  const { getters } = context

  const { isFirstPage, isLastPage, shouldResetState } = options
  const getPostById = getters.getPostByServerId

  const isOnlyPage = isFirstPage && isLastPage
  const newPostEntitiesByCid = shouldResetState || isOnlyPage ? {} : { ...getters.postEntitiesByCid }

  // When we are just updating the state (no reset), there is 1 case we should care about:
  // Auto refresh is triggered after our page goes to the background and then comes to the top again (e.g. user switch browser tabs)
  // There maybe records deleted in the backend but not in the frontend => delete them
  // skip this check for the initial posts fetch when the page is first loaded
  if (!shouldResetState && getters.isInitialPaginatedDataLoadDone && newPosts.length) {
    const cidsToDelete = getPostCidsToDeleteInPage(isFirstPage, isLastPage, newPosts, newPostEntitiesByCid)
    cidsToDelete.forEach((cid: string): void => {
      delete newPostEntitiesByCid[cid]
    })
  }

  newPosts.forEach((postData) => {
    if (getters.isPostMarkedAsDeleted(postData.attributes)) {
      delete newPostEntitiesByCid[addCidIfAbsent(postData.attributes).cid]
      return
    }
    const post = postData.attributes
    const existingPost = getPostById(post.id)
    if (!existingPost) {
      // Add new post
      const postWithCid = addCidIfAbsent(post)
      newPostEntitiesByCid[postWithCid.cid] = postWithCid
    } else if (isPostRequestPending(existingPost, context)) {
      // Don't overwrite if there are changes that will be persisted
      newPostEntitiesByCid[existingPost.cid] = existingPost
    } else {
      // Update the post otherwise
      newPostEntitiesByCid[existingPost.cid] = {
        ...post,
        cid: existingPost.cid,
      }
    }
  })

  // Don't let unsaved posts disappear.
  getters.postsArray.forEach((post) => {
    if (isNew(post) && !newPostEntitiesByCid[post.cid]) {
      newPostEntitiesByCid[post.cid] = post
    }
  })

  return newPostEntitiesByCid
}

/********************************
 * POST SAVING
 ********************************/

interface CommitAndDispatch {
  commit: Commit
  dispatch: Dispatch
}

enum PostConfiguration {
  All,
  BodyAndOrAttachment,
  BodyOrAttachment,
}

interface CopyXferPostParams {
  wallId?: Id
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall?: boolean
  postPosition?: { left: number; top: number }
  postId: Id
  transferredPostCid?: Cid
  libraryId?: string
}

const DEFAULT_RETRY_DELAY = 500
function wrapMethodWithRetries<T>(
  method: () => Promise<T>,
  maxRetries = 0,
  delayInMs = DEFAULT_RETRY_DELAY,
): () => Promise<T> {
  let retriesLeft = maxRetries

  return (): Promise<T> =>
    new Promise((resolve, reject): void => {
      async function methodWithRetries(): Promise<any> {
        try {
          const result = await method()
          return resolve(result)
        } catch (e) {
          const isClientError = e.status >= 400 && e.status < 500
          const toRetry = !isClientError && retriesLeft > 0
          if (toRetry) {
            retriesLeft -= 1
            setTimeout((): void => {
              methodWithRetries()
            }, delayInMs)
          } else {
            reject(e)
          }
          return null
        }
      }
      methodWithRetries()
    })
}

// Cached PostSavers
const postSaverById: { [id: number]: PostSaver } = {}
const postSaverByCid: { [cid: string]: PostSaver } = {}

// These are the standard requests. You may define your own strings as custom keys.
enum PostRequestKey {
  Create = 'Create',
  // Update post with attributes at the time when request is enqueued
  UpdateCurrent = 'UpdateCurrent',
  // Update post with attributes at the time when request is executed
  UpdateLatest = 'UpdateLatest',
  Delete = 'Delete',
}

interface CustomSavePostRequest {
  key: string
  method: (post?: Post) => Promise<any>
}
interface Cancelable {
  cancel(): void
  flush(): void
}

/*****************************************************

PostSaver ensures that only one post update/create event is being made at a time.
After instantiation, you may call `save` on it, or enqueue a request:

```
const saver = new PostSaver(post, dispatch)
saver.save() // create immediately if new
// ...
saver.save() // does not fire request if one is still in progress
// ...
saver.save() // updates post if post has been persisted
saver.save() // debounces updates
saver.customSave({ key: 'A_POST_RELATED_REQUEST', method: () => { ... } }) // custom request queued
saver.save() // update request is queue after custom request
```

*****************************************************/

class PostSaver {
  public isUpdateDebouncing = false
  private q: PromiseQueue
  private dispatch!: Dispatch
  private getters!: any
  // Store both id and cid so that we can use the same queue before and after the post is saved
  private postId!: Id | null
  private postCid!: Cid
  private _debouncedEnqueueUpdate!: null | ((() => void) & Cancelable) // lazily created

  public constructor({ id, cid }: Post, { dispatch: PostModuleDispatch, getters: PostModuleGetters }) {
    this.postId = id || null
    this.postCid = cid
    this.dispatch = PostModuleDispatch
    this.getters = PostModuleGetters
    this.q = new PromiseQueue()
  }

  public get isIdle(): boolean {
    return this.q.isEmpty && !this.isUpdateDebouncing
  }

  public save(): Post {
    const post = this.post
    if (isEmpty(post)) return post
    if (isNew(post) && this.q.currentKey !== PostRequestKey.Create) {
      this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    } else {
      this.debouncedEnqueueUpdate()
    }
    return post
  }

  public delete(): void {
    this.q.enqueue(PostRequestKey.Delete, this.deletePost.bind(this))
  }

  public customSave({ key, method }: CustomSavePostRequest): Promise<any> {
    // Ensure post has been is created
    if (isNew(this.post) && this.q.currentKey !== PostRequestKey.Create) {
      this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    }
    // Post should be updated to current state before the new request is made
    // Convert UpdateLatest request to an UpdateCurrent request to lock in the current state
    this.flushUpdates()
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) {
      this.q.unqueue()
      const currentPost = cloneDeep(this.post)
      const updateCurrentPost = (): Promise<any> => this.updatePost(currentPost)
      this.q.enqueue(PostRequestKey.UpdateCurrent, updateCurrentPost)
    }

    // Wrap method to pass in latest post. Request might require id but was queued before post was saved.
    const wrappedMethod = (): Promise<any> => {
      // If post has been deleted, skip subsequent operations.
      if (this.isPostMarkedAsDeleted) return Promise.resolve()
      return method(this.post)
    }

    // Finally, queue the method.
    return this.q.enqueue(key, wrappedMethod)
  }

  public flushUpdates() {
    if (!this._debouncedEnqueueUpdate) return
    this._debouncedEnqueueUpdate.flush()
  }

  // PRIVATE

  // Get the latest post
  private get post(): Post {
    return this.getters.getLiveOrDeletedPost({ cid: this.postCid, id: this.postId })
  }

  private get postOrder(): Cid[] {
    return this.getters.postOrder
  }

  private get isPostMarkedAsDeleted(): Post {
    return this.getters.isPostMarkedAsDeleted({ cid: this.postCid, id: this.postId })
  }

  private debouncedEnqueueUpdate(): void {
    this.isUpdateDebouncing = true
    if (!this._debouncedEnqueueUpdate) {
      this._debouncedEnqueueUpdate = debounce(this.enqueueUpdate, 500)
    }
    return this._debouncedEnqueueUpdate()
  }

  private enqueueUpdate(): void {
    this.isUpdateDebouncing = false
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) return
    this.q.enqueue(PostRequestKey.UpdateLatest, this.updatePost.bind(this))
  }

  private async createPost(): Promise<any> {
    if (this.isPostMarkedAsDeleted) return

    const post = this.post
    // Get the true post order from the current postOrder that is in the store state.
    // Then get the array of postOrder that only contains the cid of the current postOrder
    // And only posts with ID (not pending creation)
    let postCreationParams: Post & UpdatePostArgs = post
    if (isAppUsing('wishOrderArray')) {
      const postOrderByCids: Cid[] = this.postOrder.filter((cid: Cid) => {
        if (cid === post.cid) return true
        const p = this.getters.getPostByCid(cid)
        if (isNew(p)) return false
        return true
      })
      const postIndex = postOrderByCids.indexOf(post.cid)
      const prevPost = this.getters.getPostByCid(postOrderByCids[postIndex - 1])
      const nextPost = this.getters.getPostByCid(postOrderByCids[postIndex + 1])
      postCreationParams = {
        ...post,
        wish_order: {
          previous: postIndex === 0 ? null : prevPost.id,
          next: postIndex === postOrderByCids.length - 1 ? null : nextPost.id,
        },
      }
    }
    try {
      const createPostWithRetries = wrapMethodWithRetries(
        (): Promise<{ data: { attributes: Post & UpdatePostArgs } }> => PadletApi.Wish.create(postCreationParams),
        1,
      )
      const createPostResult = (await createPostWithRetries()).data.attributes
      const { id } = createPostResult
      if (isAppUsing('wishOrderArray')) {
        const { wish_order: wishOrder } = createPostResult
        this.dispatch('createdPostSuccess', { ...post, id, wish_order: wishOrder })
      }
      if (!isAppUsing('wishOrderArray')) {
        this.dispatch('createdPostSuccess', { ...post, id })
      }
      this.postId = id as Id
      postSaverById[id as Id] = this
      return { id }
    } catch (e) {
      if (e.status === 403) {
        this.dispatch('showReloadNotification', null, { root: true }) // Ask to reload if no permissions
      } else {
        this.showSaveFailureSnackbar()
      }
      const context = {
        response: e.message,
        attachment: post.attachment,
        requestBody: JSON.stringify(postCreationParams),
      }
      captureMessage(`CreatedPostError ${e.status || ''}`, { context })
      return null
    }
  }

  private async updatePost(post?: Post): Promise<any> {
    if (this.isPostMarkedAsDeleted) return

    // If not specific version of the post is provided, fetch the latest from the store
    const clientPost = post ? { ...post, id: this.post.id } : this.post
    if (isEmpty(clientPost)) return null
    try {
      const savedPost = (await PadletApi.Wish.update(clientPost)).data.attributes // Already retries
      this.dispatch('updatedPostSuccess', { ...savedPost, ...clientPost })
      return savedPost
    } catch (e) {
      const context = { response: e.message, postId: clientPost.id }
      if (e.status === 404) {
        this.dispatch('refresh', null, { root: true }) // Refresh if post deleted
      } else if (e.status === 403) {
        this.dispatch('showReloadNotification', null, { root: true }) // Ask to reload if no permissions
        captureMessage('UpdatedPostError 403', { context })
      } else {
        this.showSaveFailureSnackbar()
        captureMessage(`UpdatedPostError ${e.status || ''}`, { context })
      }
      return null
    }
  }

  private async deletePost(): Promise<void> {
    try {
      if (!this.postId) return Promise.resolve()
      await PadletApi.Wish.delete({ id: this.postId })
    } catch (e) {
      const context = { response: e.message, postId: this.postId }
      if (e.status === 404) {
        this.dispatch('refresh', null, { root: true })
      } else if (e.status === 403) {
        this.dispatch('showReloadNotification', null, { root: true }) // Ask to reload if no permissions
        captureMessage('DeletedPostError 403', { context })
      } else {
        this.showSaveFailureSnackbar()
        captureMessage(`DeletedPostError ${e.status || ''}`, { context })
      }
    }
  }

  private showSaveFailureSnackbar(): void {
    this.dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
  }
}

// Assumes post has a Cid.
function getOrCreatePostSaver(post: Post, context): PostSaver {
  // Prefer to look up post by server id since cid changes after initial creation.
  // Assume that when post is successfully saved, it will be populated in postSaverById.
  let saver = (post.id && postSaverById[post.id]) || postSaverByCid[post.cid]
  if (saver) return saver

  saver = new PostSaver(post, context)
  if (post.id) {
    postSaverById[post.id] = saver
  } else {
    postSaverByCid[post.cid] = saver
  }
  return saver
}

function isPostRequestPending(post: Post, context): boolean {
  const postSaver = getOrCreatePostSaver(post, context)
  return !postSaver.isIdle
}

const savePost = (context, post: PostAttributes & UpdatePostArgs): Post => {
  const { dispatch, getters } = context
  // Ensure that: (1) post had cid, (2) post is added to store, (3) post in store is latest version
  dispatch(isNew(post) ? 'addPost' : 'updatePostInStore', post)
  const postWithCid = post.cid ? post : getters.getPostByServerId(post.id)
  return getOrCreatePostSaver(postWithCid, context).save()
}

function customSavePost(context, post: Post, request: CustomSavePostRequest): Promise<any> {
  return getOrCreatePostSaver(post, context).customSave(request)
}

function deletePost(context: CommitAndDispatch, post: Post): void {
  context.dispatch('removePost', post)
  getOrCreatePostSaver(post, context).delete()
}

/**
 * Delete all posts using PadletApi abstraction
 */
function deleteAllPosts(context: CommitAndDispatch, posts: Post[]): void {
  posts.forEach((post): void => {
    deletePost(context, post)
  })
}

async function copyPost({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const copyFunc = PadletApi.Wish.copy.bind(PadletApi.Wish)
  const { wallId, wallDescription, wallTitle, wallSectionId, postPosition, libraryId } = params
  try {
    const postAttributes = (
      await copyFunc({
        wishId: params.postId,
        wallId,
        wallDescription,
        wallTitle,
        wallSectionId,
        postPosition,
        libraryId,
      })
    ).data.attributes
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch(
      'postAction/updatePostCopied',
      {
        newPostData: postAttributes,
        originalPostId: params.postId,
        isDestinationNewWall,
      },
      { root: true },
    )
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostCopied', { error: parsedError }, { root: true })
    }
  }
}

async function transferPost({ dispatch, rootGetters }, params: CopyXferPostParams): Promise<void> {
  const { wallId, wallDescription, wallTitle, wallSectionId, isDestinationThisWall, transferredPostCid, libraryId } =
    params
  const transferFunc = PadletApi.Wish.transfer.bind(PadletApi.Wish)

  let postBeingTransferred = null
  if (!isDestinationThisWall) {
    // Optimistically remove the post being transferred to a different wall
    postBeingTransferred = rootGetters['post/getPostByCid'](transferredPostCid)
    dispatch('removePost', { cid: transferredPostCid }, { root: true })
  }

  try {
    // can transfer intra-wall, between shelf columns
    const postAttributes = (
      await transferFunc({
        wishId: params.postId,
        wallId,
        wallDescription,
        wallTitle,
        wallSectionId,
        libraryId,
        // postPosition,
      })
    ).data.attributes
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch(
      'postAction/updatePostTransferred',
      {
        newPostData: postAttributes,
        originalPostId: params.postId,
        isDestinationThisWall,
        isDestinationNewWall,
        transferredPostCid,
      },
      { root: true },
    )
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      if (!isDestinationThisWall) {
        // Reinstate the removed post since the transfer failed
        dispatch('post/addPost', postBeingTransferred, { root: true })
      }
      dispatch('postAction/updatePostTransferred', { error: parsedError }, { root: true })
    }
  }
}

/********************************
 * ID/CID HELPERS
 ********************************/
/**
 * Maintains the cid of a post if it's present. Generate one otherwise.
 */
function addCidIfAbsent(post: PostAttributes): Post {
  if (post.cid) return post as Post
  const cid = post.id ? `c${post.id}` : uniqueId('c_new')
  return { ...post, cid }
}

export {
  PostConfiguration,
  CopyXferPostParams,
  deletePost,
  deleteAllPosts,
  copyPost,
  transferPost,
  savePost,
  addCidIfAbsent,
  isNew,
  isEmpty,
  hasAttachment,
  isNonEmpty,
  dumpPostContentHashString,
  loadPostContentHashString,
  getPostCidsToDeleteInPage,
  reconcilePostsAfterFetch,
  customSavePost,
  CustomSavePostRequest,
  getOrCreatePostSaver,
  VALID_COLORS,
}
